import { Button, Icon, Tag, Text, Tooltip } from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useState } from 'react';
import { createColumnHelper } from '@/components/common/table/appTable';
import { useRestrictedKeyRequests } from '@/hooks/application';
import type { RestrictedKeyRequestResponse } from '@/hooks/application/restricted_key_requests_resource';
import { useCanWriteById } from '@/hooks/workspace/use_workspace_access';
import { dateTimeFormat } from '@/utility/date';
import { TableStoreProvider } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { UserNameLink } from '../common/UserNameLink';
import { CreateRestrictedKeyRequestDialog } from './CreateRestrictedKeyRequestDialog';

type Props = {
  workspaceId: number;
  applicationId: number;
};

const statusColorScheme = (status: string): string => {
  if (status === 'APPROVED') return 'success';
  if (status === 'DENIED') return 'error';
  return 'warning';
};

const formatProperty = (row: RestrictedKeyRequestResponse): string =>
  row.capability ? `${row.property}:${row.capability}` : row.property;

const columnHelper = createColumnHelper<RestrictedKeyRequestResponse>();

const restrictedKeyRequestColumns = columnHelper.columns([
  columnHelper.accessor('id', {
    cell: (info) => (
      <Text fontFamily="mono" fontSize="sm">
        {info.getValue()}
      </Text>
    ),
    header: 'ID',
    size: 80,
  }),
  columnHelper.display({
    id: 'property',
    cell: (info) => (
      <Text fontFamily="mono" fontSize="sm">
        {formatProperty(info.row.original)}
      </Text>
    ),
    header: 'Property',
  }),
  columnHelper.display({
    id: 'status',
    cell: (info) => {
      const status = info.row.original.status;
      const denialReason = info.row.original.denialReason;
      const tag = (
        <Tag colorScheme={statusColorScheme(status)} size="sm">
          {status}
        </Tag>
      );
      if (status === 'DENIED' && denialReason) {
        return <Tooltip label={`Reason: ${denialReason}`}>{tag}</Tooltip>;
      }
      return tag;
    },
    header: 'Status',
    size: 140,
  }),
  columnHelper.accessor('requestReason', {
    cell: (info) => (
      <Tooltip label={info.getValue()}>
        <Text fontSize="sm" noOfLines={1}>
          {info.getValue()}
        </Text>
      </Tooltip>
    ),
    header: 'Justification',
  }),
  columnHelper.accessor('requesterOid', {
    cell: (info) => <UserNameLink oid={info.getValue()} />,
    enableSorting: false,
    header: 'Requester',
  }),
  columnHelper.accessor('createdAt', {
    cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
    header: 'Created At',
  }),
]);

export const RestrictedKeyRequestTable = (props: Props) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const canWrite = useCanWriteById(props.workspaceId);
  const { data, error, isLoading, mutate, isValidating } = useRestrictedKeyRequests(
    props.workspaceId,
    props.applicationId
  );

  return (
    <>
      <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="RestrictedKeyRequest">
        <Table
          addButton={
            canWrite
              ? () => (
                  <Tooltip label="Request access to a restricted property" placement="left">
                    <Button
                      borderRightRadius="0px"
                      leftIcon={<Icon as={PlusBold} />}
                      onClick={() => setIsCreateDialogOpen(true)}
                      size="sm"
                      variant="outline"
                    >
                      Request Access
                    </Button>
                  </Tooltip>
                )
              : undefined
          }
          columns={restrictedKeyRequestColumns}
          count={data?.itemCount || 0}
          data={data?.restrictedKeyRequests || []}
          isLoading={Boolean(isLoading || !data || error)}
          isValidating={isValidating}
          refresh={mutate}
          title="Restricted Key Requests"
        />
      </TableStoreProvider>
      <CreateRestrictedKeyRequestDialog
        applicationId={props.applicationId}
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={mutate}
        workspaceId={props.workspaceId}
      />
    </>
  );
};
